import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "urql"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import {
  alertRuleSchema,
  alertChannelValues,
  alertChannelLabels,
  type AlertRuleFormValues,
  type AlertChannelValue,
} from "@/lib/schemas"
import { CreateAlertRuleMutation, UpdateAlertRuleMutation } from "@/lib/graphql/mutations"
import type {
  AlertChannel,
  CreateAlertRuleInput,
  UpdateAlertRuleInput,
} from "@/lib/generated/graphql"

type EmailAlertConfig = {
  to: string[]
  subjectTemplate?: string | null
}

type SlackAlertConfig = {
  webhookUrl: string
  channel?: string | null
}

interface AlertRuleFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  initialData?: {
    id: string
    name: string
    conditionType: string
    threshold: number
    webhookUrl: string
    channel: AlertChannel
    emailConfig?: EmailAlertConfig | null
    slackConfig?: SlackAlertConfig | null
    cooldown: string
    enabled: boolean
  }
}

const CONDITION_TYPES = [
  { value: "dlq_threshold", label: "DLQ Threshold" },
  { value: "outbound_dlq_threshold", label: "Outbound DLQ Threshold" },
  { value: "backpressure", label: "Backpressure" },
] as const

function buildDefaults(initialData: AlertRuleFormProps["initialData"]): AlertRuleFormValues {
  const channel = (initialData?.channel ?? "WEBHOOK") as AlertChannelValue
  return {
    name: initialData?.name ?? "",
    conditionType: (initialData?.conditionType as AlertRuleFormValues["conditionType"]) ?? "dlq_threshold",
    threshold: initialData?.threshold ?? 100,
    channel,
    webhookUrl: channel === "WEBHOOK" ? (initialData?.webhookUrl ?? "") : "",
    emailTo: channel === "EMAIL" ? (initialData?.emailConfig?.to.join(", ") ?? "") : "",
    emailSubjectTemplate: channel === "EMAIL" ? (initialData?.emailConfig?.subjectTemplate ?? "") : "",
    slackWebhookUrl: channel === "SLACK" ? (initialData?.slackConfig?.webhookUrl ?? "") : "",
    slackChannel: channel === "SLACK" ? (initialData?.slackConfig?.channel ?? "") : "",
    cooldown: initialData?.cooldown ?? "1h",
    enabled: initialData?.enabled ?? true,
  }
}

export function AlertRuleForm({ open, onOpenChange, onSuccess, initialData }: AlertRuleFormProps) {
  const isEdit = !!initialData
  const [, createRule] = useMutation(CreateAlertRuleMutation)
  const [, updateRule] = useMutation(UpdateAlertRuleMutation)

  const form = useForm<AlertRuleFormValues>({
    resolver: zodResolver(alertRuleSchema) as Resolver<AlertRuleFormValues>,
    defaultValues: buildDefaults(initialData),
  })

  const selectedChannel = form.watch("channel") ?? "WEBHOOK"

  useEffect(() => {
    if (open) {
      form.reset(buildDefaults(initialData))
    }
  }, [open, initialData, form])

  const onSubmit = async (values: AlertRuleFormValues) => {
    const channel = values.channel ?? "WEBHOOK"

    const base = {
      name: values.name,
      conditionType: values.conditionType,
      threshold: values.threshold,
      channel,
      cooldown: values.cooldown,
      enabled: values.enabled,
    }

    let channelFields: Pick<CreateAlertRuleInput, "webhookUrl" | "emailConfig" | "slackConfig"> = {}
    if (channel === "WEBHOOK") {
      channelFields = { webhookUrl: values.webhookUrl }
    } else if (channel === "EMAIL") {
      const recipients = (values.emailTo ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      channelFields = {
        emailConfig: {
          to: recipients,
          subjectTemplate: values.emailSubjectTemplate || undefined,
        },
      }
    } else if (channel === "SLACK") {
      channelFields = {
        slackConfig: {
          webhookUrl: values.slackWebhookUrl ?? "",
          channel: values.slackChannel || undefined,
        },
      }
    }

    if (isEdit) {
      const input: UpdateAlertRuleInput = { ...base, ...channelFields }
      const result = await updateRule({ id: initialData.id, input })
      if (result.error) {
        toast.error(result.error.message)
        return
      }
      toast.success("Alert rule updated")
    } else {
      const input: CreateAlertRuleInput = { ...base, ...channelFields }
      const result = await createRule({ input })
      if (result.error) {
        toast.error(result.error.message)
        return
      }
      toast.success("Alert rule created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Alert Rule" : "Create Alert Rule"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="DLQ size alert" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>

            <div className="space-y-2">
              <Label>Condition</Label>
              <Select
                value={form.watch("conditionType")}
                onValueChange={(v) => form.setValue("conditionType", v as AlertRuleFormValues["conditionType"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_TYPES.map((ct) => (
                    <SelectItem key={ct.value} value={ct.value}>
                      {ct.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="threshold">Threshold</Label>
                <Input
                  id="threshold"
                  type="number"
                  {...form.register("threshold")}
                />
                <FieldError error={form.formState.errors.threshold} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cooldown">Cooldown</Label>
                <Input
                  id="cooldown"
                  className="font-mono"
                  placeholder="1h"
                  {...form.register("cooldown")}
                />
                <FieldError error={form.formState.errors.cooldown} />
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label>Channel</Label>
              <Select
                value={selectedChannel}
                onValueChange={(v) => form.setValue("channel", v as AlertChannelValue, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {alertChannelValues.map((ch) => (
                    <SelectItem key={ch} value={ch}>
                      {alertChannelLabels[ch]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedChannel === "WEBHOOK" && (
              <div className="space-y-2">
                <Label htmlFor="webhookUrl">Webhook URL</Label>
                <Input
                  id="webhookUrl"
                  className="font-mono text-xs"
                  placeholder="https://hooks.example.com/alerts"
                  {...form.register("webhookUrl")}
                />
                <FieldError error={form.formState.errors.webhookUrl} />
              </div>
            )}

            {selectedChannel === "EMAIL" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="emailTo">Recipients</Label>
                  <Input
                    id="emailTo"
                    className="text-sm"
                    placeholder="alice@example.com, bob@example.com"
                    {...form.register("emailTo")}
                  />
                  <p className="text-xs text-muted-foreground">Comma-separated email addresses</p>
                  <FieldError error={form.formState.errors.emailTo} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailSubjectTemplate">Subject Template</Label>
                  <Input
                    id="emailSubjectTemplate"
                    className="text-sm"
                    placeholder="[Hivehook] DLQ threshold exceeded"
                    {...form.register("emailSubjectTemplate")}
                  />
                  <p className="text-xs text-muted-foreground">Optional custom subject line</p>
                </div>
              </>
            )}

            {selectedChannel === "SLACK" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="slackWebhookUrl">Slack Webhook URL</Label>
                  <Input
                    id="slackWebhookUrl"
                    className="font-mono text-xs"
                    placeholder="https://hooks.slack.com/services/..."
                    {...form.register("slackWebhookUrl")}
                  />
                  <FieldError error={form.formState.errors.slackWebhookUrl} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slackChannel">Channel Override</Label>
                  <Input
                    id="slackChannel"
                    className="text-sm"
                    placeholder="#alerts"
                    {...form.register("slackChannel")}
                  />
                  <p className="text-xs text-muted-foreground">Optional channel override</p>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
